/**
 * Card displaying pending action items from consultations
 */

import { useState } from 'react';
import { CheckSquare, Square, AlertTriangle, Calendar, User } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { usePendingActionItems, useActionItemMutations } from '@/hooks/use-consultations';
import { cn } from '@/lib/utils';

interface ActionItemsCardProps {
  clientId: string;
}

export function ActionItemsCard({ clientId }: ActionItemsCardProps) {
  const { data: actionItems = [], isLoading } = usePendingActionItems(clientId);
  const { toggleActionItem } = useActionItemMutations();

  const handleToggle = (id: string, currentState: boolean) => {
    toggleActionItem.mutate({ id, is_completed: !currentState });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (actionItems.length === 0) {
    return null;
  }

  const overdueItems = actionItems.filter(item => item.is_overdue);
  const upcomingItems = actionItems.filter(item => !item.is_overdue);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckSquare className="h-5 w-5" />
          Action Items
          {overdueItems.length > 0 && (
            <Badge variant="destructive" className="ml-2">
              {overdueItems.length} overdue
            </Badge>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Overdue Items */}
        {overdueItems.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-destructive flex items-center gap-1 mb-2">
              <AlertTriangle className="h-4 w-4" />
              Overdue
            </h4>
            <div className="space-y-2">
              {overdueItems.map((item) => (
                <ActionItemRow
                  key={item.id}
                  item={item}
                  onToggle={handleToggle}
                  isOverdue
                />
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Items */}
        {upcomingItems.length > 0 && (
          <div>
            {overdueItems.length > 0 && (
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Upcoming</h4>
            )}
            <div className="space-y-2">
              {upcomingItems.map((item) => (
                <ActionItemRow
                  key={item.id}
                  item={item}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActionItemRow({
  item,
  onToggle,
  isOverdue,
}: {
  item: {
    id: string;
    description: string;
    due_date: string | null;
    assigned_to: string | null;
    consultation_date: string;
  };
  onToggle: (id: string, currentState: boolean) => void;
  isOverdue?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 p-2 rounded-md border",
        isOverdue ? "border-destructive/30 bg-destructive/5" : "bg-muted/30"
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 mt-0.5"
        onClick={() => onToggle(item.id, false)}
      >
        <Square className="h-4 w-4" />
      </Button>

      <div className="flex-1 min-w-0">
        <p className="text-sm">{item.description}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          {item.due_date && (
            <span className={cn(
              "flex items-center gap-1",
              isOverdue && "text-destructive font-medium"
            )}>
              <Calendar className="h-3 w-3" />
              Due {format(new Date(item.due_date), 'MMM d')}
            </span>
          )}
          <span className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {item.assigned_to === 'trainer' ? 'You' : 'Client'}
          </span>
          <span>
            From {format(new Date(item.consultation_date), 'MMM d')} consultation
          </span>
        </div>
      </div>
    </div>
  );
}
